import React from 'react'
import clsx from 'clsx'

// Props für eine einzelne Skill-Kachel
export type SkillTileItem = {
  id: string
  title: string
  icon?: string | React.ReactNode
  status?: 'not_attempted' | 'in_progress' | 'not_yet_competent' | 'competent' | 'proficient'
  color?: string // Hintergrundfarbe für hierarchische Gruppierung
  borderColor?: string // Randfarbe
  evidenceCount?: number // Anzahl der Belege
}

// Props für die SkillGrid Komponente
export type SkillGridProps = {
  items: SkillTileItem[]
  className?: string
  onTileClick?: (item: SkillTileItem) => void
}

// Einzelne Skill-Kachel Komponente
const SkillTile: React.FC<{ item: SkillTileItem; onClick?: () => void }> = ({ item, onClick }) => {
  const isLocked = item.status === 'not_attempted'
  const isCompetent = item.status === 'competent' || item.status === 'proficient'

  // Bestimme Hintergrundfarbe und Randfarbe basierend auf item.color und item.borderColor
  const backgroundColor = item.color || 'white'
  const borderColor = item.borderColor || '#e5e7eb' // gray-200
  const borderWidth = item.borderColor ? '2px' : '1px'

  return (
    <div
      onClick={onClick}
      className={clsx(
        'group relative flex flex-col items-center justify-center rounded-lg transition-all duration-200',
        'cursor-pointer',
        'shadow-sm',
        'hover:shadow-md hover:scale-105',
        'active:scale-100',
        isLocked && 'opacity-60',
        isCompetent && 'ring-2 ring-green-200 ring-opacity-50'
      )}
      style={{
        width: '100%',
        aspectRatio: '1',
        minHeight: '120px',
        maxHeight: '200px',
        backgroundColor,
        border: `${borderWidth} solid ${borderColor}`
      }}
    >
      {/* Icon oder Emoji oder Evidence Count */}
      {item.icon && (
        <div className={clsx(
          'mb-2 flex items-center justify-center',
          // Wenn Icon eine Zahl ist (Evidence Count), größer und fetter darstellen
          typeof item.icon === 'string' && /^\d+$/.test(item.icon)
            ? 'text-3xl font-bold text-blue-700'
            : 'text-2xl'
        )}>
          {typeof item.icon === 'string' ? (
            <span>{item.icon}</span>
          ) : (
            item.icon
          )}
        </div>
      )}

      {/* Titel */}
      <h3
        className={clsx(
          'px-2 text-center text-xs font-medium leading-tight',
          'break-words hyphens-auto',
          'line-clamp-3',
          isLocked ? 'text-gray-400' : 'text-gray-700',
          'group-hover:text-gray-900'
        )}
        style={{
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          WebkitHyphens: 'auto',
          hyphens: 'auto'
        }}
        title={item.title}
      >
        {item.title}
      </h3>

      {/* Status-Indikator (optional, sehr dezent) - oben links */}
      {item.status && (
        <div className="absolute top-1 left-1">
          <div
            className={clsx(
              'h-2 w-2 rounded-full',
              item.status === 'proficient' && 'bg-green-500',
              item.status === 'competent' && 'bg-blue-500',
              item.status === 'in_progress' && 'bg-yellow-500',
              item.status === 'not_yet_competent' && 'bg-orange-500',
              item.status === 'not_attempted' && 'bg-gray-300'
            )}
          />
        </div>
      )}
    </div>
  )
}

// Grid-Komponente
export const SkillGrid: React.FC<SkillGridProps> = ({ items, className, onTileClick }) => {
  return (
    <div
      className={clsx(
        'grid gap-2',
        'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7',
        className
      )}
    >
      {items.map((item) => (
        <SkillTile 
          key={item.id} 
          item={item} 
          onClick={onTileClick ? () => onTileClick(item) : undefined}
        />
      ))}
    </div>
  )
}

